import { Preferences } from '@capacitor/preferences';
import CatacombScene from 'src/app/Scenes/CatacombScene';
import MainScene from 'src/app/Scenes/MainScene';
import MazeScene from 'src/app/Scenes/MazeScene';
import RestInPiecesScene from 'src/app/Scenes/RestInPiecesScene';
import PumpHouseScene from 'src/app/Scenes/PumpHouseScene';
import ChurchHillScene from 'src/app/Scenes/ChurchHillScene';
import VCUEngineeringScene from 'src/app/Scenes/VCUEngineeringScene';
import BaseLevelScene from 'src/app/Scenes/BaseLevelScene';
import CastleScene from 'src/app/Scenes/CastleScene';
import { CustomDialog, DialogInformer } from './UI/CustomDialog';
import { BaseSpriteInterface } from './SpriteInterfaces';
import { Inventory } from './UI/Inventory';
import { StateManager } from './StateManager';
import { PlacesList } from './dataClasses/Places';
import { WebsocketService } from 'src/app/services/websocket.service';
import { PlayerMetadata } from './PlayerMetadata';

export interface PortalDataInterface extends BaseSpriteInterface{
  width: number;
  height: number;
  portalName: string;
  prerequisites: Array<string>;
  borderless?: boolean;
  requiresHealth?: boolean;
  requiresKey?: boolean;
  musicTrack?: string;
}

export class Portal extends Phaser.Physics.Matter.Sprite implements DialogInformer{
  portalName: string;
  static observers: Array<any> = [];
  static visitedPlaces: Array<string> = [];
  static gpsVisitedPlaces: Array<string> = [];
  public playerOffsetX: number = 0;
  public playerOffsetY: number = 0;
  dialog: CustomDialog;
  dialogContent: string;
  url: string;
  prerequisites: Array<string> = [];
  requiresHealth: boolean = false;
  requiresKey: boolean = false;
  musicTrack: string;
  private healthRequiredText = "You don't have enough health\nto enter this level";
  private keyRequiredText = "This door appears to be locked.\nYou need a key...\nDefeat all enemies to reveal it";
  constructor(data: PortalDataInterface, playerScale=1, external=false, playerOffsetX=0, playerOffsetY=0) {
    let { scene, x, y, width, height, texture, portalName, prerequisites, borderless, requiresHealth, requiresKey, musicTrack } = data;
    super(scene.matter.world, x, y, texture);
    if(requiresHealth){
      this.requiresHealth = requiresHealth;
    }
    if(requiresKey){
      this.requiresKey = requiresKey;
    }
    this.name = portalName;
    this.scene = scene as
      | MainScene
      | CatacombScene
      | MazeScene
      | RestInPiecesScene
      | PumpHouseScene
      | ChurchHillScene
      | VCUEngineeringScene
      | CastleScene;
    this.portalName = portalName;
    this.playerOffsetX = playerOffsetX;
    this.playerOffsetY = playerOffsetY;
    this.prerequisites = prerequisites;

    this.musicTrack = musicTrack;

    var yOffset = external ? this.texture.source[0].height/4 - height/2 + height/4 : 0;

    // Create sensors for entering/exiting
    const Bodies = new Phaser.Physics.Matter.MatterPhysics(scene).bodies;
    var portalSensor = Bodies.rectangle(this.x, this.y+yOffset, width, height, {
      isSensor: true,
      label: 'portalSensor',
    });

    if(external){
      // create a sensor for gps
      var gpsSensor = Bodies.circle(this.x, this.y, 1250*playerScale, {
        isSensor: true,
        label: 'gpsSensor',
      });
      

      // Combine into a compound body set on the portal
      const Body = new Phaser.Physics.Matter.MatterPhysics(scene).body;
      const compoundBody = Body.create({
        parts: [portalSensor, gpsSensor],
      });
      this.setExistingBody(compoundBody);


      // Place entrances to levels at the center bottom of each image
      const imageSprite = new Phaser.Physics.Matter.Sprite(this.scene.matter.world, this.x, this.y, texture);
      var collider = Bodies.rectangle(imageSprite.x, imageSprite.y, imageSprite.width, imageSprite.height/2, {
        isSensor: false,
        label: 'structureCollider',
      });
      imageSprite.setExistingBody(collider);
      this.scene.add.existing(imageSprite);
      imageSprite.setOrigin(0.5, 0.75);
      imageSprite.setStatic(true);
      imageSprite.setDepth(2);
      imageSprite.setVisible(true);     
      
      if(borderless){
        imageSprite.setCollidesWith([]);
      }
    }
    else{
      this.setExistingBody(portalSensor);
    }

    // add the portal the scene
    this.scene.add.existing(this);
    this.setStatic(true);
    this.setDepth(2);
    this.setVisible(false);
  }

  static preload(scene) {
    scene.load.image('cave_exit', 'assets/portals/cave_exit.png');
    scene.load.image('catacomb_exit', 'assets/portals/catacomb_exit.png');
  }

  hasPrerequisites(){
    if(this.prerequisites && this.prerequisites.length){
      for(let i=0; i< this.prerequisites.length; i++){
        const count = Inventory.getItemQuantity(this.prerequisites[i]);
        if(count < 1) return true;
      }
    }
    return false;
  }

  isLocked(){
    return this.requiresKey && !Inventory.containsAllPassages([this.name]);
    // return !(this.scene.scene.key === "mainScene" || this.name === "mainScene" || (this.scene as BaseLevelScene).levelDefeated);
  }

  async enterOrExit() {
    const health = Inventory.getItemQuantity('heart');
    const keyCount = Inventory.getItemQuantity(this.name);
    if((!this.requiresHealth || health > 0) && !this.hasPrerequisites() && !this.isLocked()){
      console.log(`Entering: ${this.portalName}`);
      BaseLevelScene.previousSceneKey = this.scene.scene.key;
      this.scene.scene.switch(this.portalName).scene;

      const newScene = this.scene.scene.get(this.portalName);
      WebsocketService.setScene(newScene as MainScene | BaseLevelScene);

      if(WebsocketService.socket && WebsocketService.socket.readyState === WebSocket.OPEN){
        console.log('Update players level globally');

        // Update the players level globally.
        MainScene.playerMetadata.levelName = this.scene.scene.key;
        MainScene.playerMetadata.lastUpdated = Date.now();
        WebsocketService.updatePlayer(MainScene.playerMetadata as PlayerMetadata);
      }
      this.scene.scene.get(this.portalName).cameras.main.fadeIn(1000, 0, 0, 0);
    }
    else if(this.hasPrerequisites()){
      this.openPrerequisitesRequiredDialog()
    }
    else if(this.isLocked()){
      this.dialog = new CustomDialog(this, "You need a key to unlock this passage.")
    }
    else{
      this.openHealthRequiredDialog();
    }
  }

  async unlockGpsEntrance() {
    if (!(Portal.gpsVisitedPlaces.indexOf(this.portalName) > -1)) {
      Portal.gpsVisitedPlaces.push(this.portalName);
      Portal.broadcast();
      await Preferences.set({
        key: 'gpsVisitedPlaces',
        value: JSON.stringify(Portal.gpsVisitedPlaces),
      });
    }
  }

  playMusicTrack(){
    if(this.musicTrack){
      const existing = this.scene.sound.get(this.musicTrack) as Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
      if(existing)
      {
        MainScene.music.stop();
        existing.play({loop: true});
        existing.setVolume(0.5);
        return;
      }
      else{
        try{
          const music = this.scene.sound.add(this.musicTrack) as Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;
          music.setVolume(0.5);
          MainScene.music.stop();
          music.play({loop: true});
        }
        catch(error){
            console.error(`Error playing music track: ${this.musicTrack}`, error);
        }
      }
    }
  }

  stopMusicTrack(){
    if(this.musicTrack){
      const existing = this.scene.sound.get(this.musicTrack);
      if(existing)
      {
        existing.stop();
        MainScene.music.play({loop: true});
      }
    }
  } 

  static async loadGpsVisitedPlaces() {
    const ret = await Preferences.get({ key: 'gpsVisitedPlaces' });
    Portal.gpsVisitedPlaces = ret.value ? JSON.parse(ret.value) : [];
  }

  static async resetGpsVisitedPlaces() {
    Portal.gpsVisitedPlaces = [];
    await Preferences.set({
      key: 'gpsVisitedPlaces',
      value: JSON.stringify([]),
    });
  }

  async updateVisitedPlace() {
    if (!(Portal.gpsVisitedPlaces.indexOf(this.portalName) > -1)) {
      Portal.gpsVisitedPlaces.push(this.portalName);
      Portal.broadcast();
      await Preferences.set({
        key: 'visitedPlaces',
        value: JSON.stringify(Portal.gpsVisitedPlaces),
      });
    }
    StateManager.updateLocation(PlacesList.find((place) => place.name === this.portalName));
  }

  static async loadVisitedPlaces() {
    const ret = await Preferences.get({ key: 'visitedPlaces' });
    Portal.gpsVisitedPlaces = ret.value ? JSON.parse(ret.value) : [];
  }

  static async resetVisitedPlaces() {
    Portal.gpsVisitedPlaces = [];
    await Preferences.set({
      key: 'visitedPlaces',
      value: JSON.stringify([]),
    });
  }

  static subscribe(fn: any) {
    Portal.observers.push(fn);
  }

  static async broadcast() {
    Portal.observers.forEach((subscriber) => subscriber());
  }

  openDialog() {
    this.dialog = new CustomDialog(this);
  }

  openHealthRequiredDialog(){
    this.dialog = new CustomDialog(this, this.healthRequiredText)
  }

  openPrerequisitesRequiredDialog(){
    const prerequisiteMessage = `You must first obtain the crystal from\n${this.prerequisites[0]} before entering.`
    this.dialog = new CustomDialog(this, prerequisiteMessage);
  }

  openKeyRequiredDialog(){
    this.dialog = new CustomDialog(this, this.keyRequiredText)
  }

  closeDialog() {
    if (this.dialog && this.dialog.dialog) this.dialog.close();
  }
}
